import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Activity, AlertTriangle, RefreshCw,
  ArrowUpRight, ArrowDownRight, Sparkles, Crown, IndianRupee, Globe,
  BarChart3, Target, ChevronRight, Menu, X, Zap, Bot, Flame,
  Clock, ArrowUp, ArrowDown, Wifi, Briefcase, Calendar, Bell
} from 'lucide-react';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid
} from 'recharts';
import { TradeIdeaTab, BlockDealsTab, EventCalendarTab, SmartAlertsTab } from './PremiumIntelligence';
import { DailyTraderQuoteSlim } from '../components/DailyTraderQuote';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// --- helpers ---
const fmt = (n, d = 2) => {
  if (n === null || n === undefined) return '—';
  const x = Number(n);
  if (!isFinite(x)) return '—';
  return x.toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d });
};
const fmtInt = (n) => {
  if (n === null || n === undefined) return '—';
  const x = Number(n);
  if (!isFinite(x)) return '—';
  return x.toLocaleString('en-IN');
};
const fmtCr = (v) => v === null || v === undefined ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;

const statusBadge = (status) => {
  const map = {
    open: { label: '● LIVE MARKET', cls: 'bg-emerald-500/20 border-emerald-500 text-emerald-300 animate-pulse' },
    pre_open: { label: '● PRE-MARKET', cls: 'bg-amber-500/20 border-amber-500 text-amber-300' },
    post_close: { label: '● POST-CLOSE', cls: 'bg-blue-500/20 border-blue-500 text-blue-300' },
    closed: { label: '● MARKETS CLOSED', cls: 'bg-gray-500/20 border-gray-500 text-gray-300' },
  };
  return map[status] || map.closed;
};

// --- Reusable bits ---
const StatChip = ({ label, data }) => {
  const ltp = data?.ltp;
  const chPct = data?.change_percent;
  const positive = (chPct ?? 0) >= 0;
  const [flash, setFlash] = React.useState(null); // 'up' / 'down' / null
  const prevLtpRef = React.useRef(ltp);

  React.useEffect(() => {
    if (prevLtpRef.current !== undefined && ltp !== undefined && ltp !== prevLtpRef.current) {
      setFlash(ltp > prevLtpRef.current ? 'up' : 'down');
      const t = setTimeout(() => setFlash(null), 900);
      prevLtpRef.current = ltp;
      return () => clearTimeout(t);
    }
    prevLtpRef.current = ltp;
  }, [ltp]);

  return (
    <div
      data-testid={`ticker-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={`relative flex-shrink-0 min-w-[160px] rounded-xl border bg-zinc-900/80 backdrop-blur px-4 py-3 transition-all duration-500 ${
        flash === 'up' ? 'border-emerald-400 shadow-lg shadow-emerald-500/40 bg-emerald-500/10' :
        flash === 'down' ? 'border-red-400 shadow-lg shadow-red-500/40 bg-red-500/10' :
        'border-golden/15 hover:border-golden/40'
      }`}
    >
      <div className="text-[10px] font-semibold text-golden/80 tracking-widest uppercase flex items-center gap-1">
        {label}
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${positive ? 'bg-emerald-400' : 'bg-red-400'} animate-pulse`}></span>
      </div>
      <div className={`text-xl font-bold text-white tracking-tight mt-1 ${flash ? 'scale-110' : 'scale-100'} transition-transform duration-300 origin-left`}>{fmt(ltp)}</div>
      <div className={`flex items-center gap-1 mt-0.5 text-xs font-medium ${positive ? 'text-emerald-400' : 'text-red-400'}`}>
        {positive ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
        {chPct !== undefined && chPct !== null ? `${Number(chPct).toFixed(2)}%` : '—'}
      </div>
    </div>
  );
};

const IndexCard = ({ label, data, accent }) => {
  const ltp = data?.ltp;
  const ch = data?.change;
  const chPct = data?.change_percent;
  const positive = (chPct ?? 0) >= 0;

  // Build a tiny intraday sparkline from whatever history we have on data (intraday[], history[]).
  // Falls back to a synthetic 8-point series shaped by sign and magnitude of the day's change.
  const sparkData = useMemo(() => {
    const series = data?.intraday || data?.history || data?.spark || null;
    if (Array.isArray(series) && series.length >= 4) {
      return series.slice(-30).map((v, i) => ({ x: i, y: Number(v.close ?? v.price ?? v.value ?? v) }));
    }
    if (ltp == null || chPct == null) return null;
    const open = Number(ltp) - Number(ch || 0);
    const pts = 8;
    // Curved synthetic path from open → ltp (so the sparkline at least signals direction)
    return Array.from({ length: pts }, (_, i) => {
      const t = i / (pts - 1);
      const mid = open + (ltp - open) * (t + 0.18 * Math.sin(t * Math.PI));
      return { x: i, y: mid };
    });
  }, [data, ltp, ch, chPct]);

  return (
    <div
      data-testid={`index-card-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={`group relative overflow-hidden rounded-xl border ${positive ? 'border-emerald-500/20' : 'border-red-500/20'} bg-gradient-to-br from-zinc-900 to-black p-4 hover:scale-[1.02] transition-all duration-300`}
    >
      <div className={`absolute -top-12 -right-12 w-32 h-32 ${positive ? 'bg-emerald-500/10' : 'bg-red-500/10'} rounded-full blur-3xl group-hover:opacity-150 transition-all`}></div>
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <span className="text-golden/80 text-[11px] font-semibold tracking-wider uppercase">{label}</span>
          {accent && <Sparkles className="w-3.5 h-3.5 text-golden" />}
        </div>
        <div className="text-white font-bold text-2xl tracking-tight">{fmt(ltp)}</div>
        <div className={`mt-1.5 flex items-center justify-between text-sm`}>
          <span className={`${positive ? 'text-emerald-400' : 'text-red-400'} font-medium flex items-center gap-1`}>
            {positive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
            {positive ? '+' : ''}{ch !== undefined && ch !== null ? fmt(ch) : '—'}
          </span>
          <span className={`${positive ? 'text-emerald-400' : 'text-red-400'} font-semibold`}>
            {positive ? '+' : ''}{chPct !== undefined && chPct !== null ? Number(chPct).toFixed(2) : '—'}%
          </span>
        </div>
        {/* Mini sparkline */}
        {sparkData && (
          <div className="mt-2 h-10 -mx-1">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData} margin={{ top: 2, right: 2, left: 2, bottom: 0 }}>
                <defs>
                  <linearGradient id={`spark-${label}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={positive ? '#34d399' : '#f87171'} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={positive ? '#34d399' : '#f87171'} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="y"
                  stroke={positive ? '#34d399' : '#f87171'}
                  strokeWidth={1.5}
                  fill={`url(#spark-${label})`}
                  isAnimationActive={false}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

// Market Mood Index — Fear & Greed style gauge built from sentiment score (0-100)
const MarketMoodIndex = ({ sentiment, breadth, vix }) => {
  const [verdict, setVerdict] = useState(null);

  // Fetch the AI verdict explaining *why* the mood is where it is. Cached server-side, so polite to call.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/market-data/mood-verdict`, { cache: 'no-store' });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled && d.verdict) setVerdict(d);
      } catch {
        /* silently fall back to numeric-only */
      }
    })();
    return () => { cancelled = true; };
  }, [sentiment?.score]);

  // Compose a 0-100 score: weighted blend of (a) backend sentiment.score, (b) advance-decline ratio, (c) VIX inverse
  const score = useMemo(() => {
    let s = Number(sentiment?.score);
    if (!Number.isFinite(s)) {
      // Fallback: synthesise from breadth + vix
      const adv = Number(breadth?.advances) || 0;
      const dec = Number(breadth?.declines) || 0;
      const tot = adv + dec || 1;
      const breadthScore = (adv / tot) * 100;
      const vixVal = Number(vix?.ltp);
      // VIX 10 = greed (high), VIX 30 = fear (low). Map inverted.
      const vixScore = Number.isFinite(vixVal) ? Math.max(0, Math.min(100, 100 - (vixVal - 10) * 5)) : 50;
      s = breadthScore * 0.7 + vixScore * 0.3;
    }
    return Math.max(0, Math.min(100, Math.round(s)));
  }, [sentiment, breadth, vix]);

  const label =
    score >= 80 ? 'Extreme Greed' :
    score >= 60 ? 'Greed' :
    score >= 40 ? 'Neutral' :
    score >= 20 ? 'Fear' : 'Extreme Fear';

  const color =
    score >= 80 ? '#10b981' :
    score >= 60 ? '#84cc16' :
    score >= 40 ? '#eab308' :
    score >= 20 ? '#f97316' : '#ef4444';

  // Gauge geometry — half circle from 180° to 0°
  const radius = 80;
  const angle = (180 - (score / 100) * 180) * (Math.PI / 180);
  const needleX = 100 + radius * 0.85 * Math.cos(angle);
  const needleY = 100 - radius * 0.85 * Math.sin(angle);

  return (
    <div className="rounded-xl border border-golden/20 bg-gradient-to-br from-zinc-900 to-black p-5" data-testid="market-mood-index">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-bold text-sm uppercase tracking-wider">Market Mood Index</h3>
        <span className="text-[10px] text-gray-400">Fear ↔ Greed</span>
      </div>
      <div className="relative w-full" style={{ aspectRatio: '2 / 1', maxWidth: 280, margin: '0 auto' }}>
        <svg viewBox="0 0 200 110" className="w-full">
          {/* Gradient arc segments */}
          <defs>
            <linearGradient id="mood-gradient" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="25%" stopColor="#f97316" />
              <stop offset="50%" stopColor="#eab308" />
              <stop offset="75%" stopColor="#84cc16" />
              <stop offset="100%" stopColor="#10b981" />
            </linearGradient>
          </defs>
          {/* Half-circle gauge background */}
          <path d="M 20,100 A 80,80 0 0 1 180,100" fill="none" stroke="url(#mood-gradient)" strokeWidth="14" strokeLinecap="round" />
          {/* Tick marks */}
          {[0, 25, 50, 75, 100].map(v => {
            const a = (180 - (v / 100) * 180) * (Math.PI / 180);
            const x1 = 100 + (radius + 8) * Math.cos(a);
            const y1 = 100 - (radius + 8) * Math.sin(a);
            const x2 = 100 + (radius - 8) * Math.cos(a);
            const y2 = 100 - (radius - 8) * Math.sin(a);
            return <line key={v} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#52525b" strokeWidth="1" />;
          })}
          {/* Needle */}
          <line x1="100" y1="100" x2={needleX} y2={needleY} stroke={color} strokeWidth="3" strokeLinecap="round" />
          <circle cx="100" cy="100" r="6" fill={color} />
          <circle cx="100" cy="100" r="3" fill="#000" />
          {/* Scale labels */}
          <text x="20" y="108" fontSize="7" fill="#71717a" textAnchor="middle">Fear</text>
          <text x="100" y="20" fontSize="7" fill="#71717a" textAnchor="middle">Neutral</text>
          <text x="180" y="108" fontSize="7" fill="#71717a" textAnchor="middle">Greed</text>
        </svg>
      </div>
      <div className="text-center mt-2">
        <div className="text-4xl font-bold" style={{ color }}>{score}</div>
        <div className="text-sm font-semibold" style={{ color }}>{label}</div>
      </div>

      {/* AI verdict — the "why" behind the number */}
      {verdict?.verdict && (
        <div className="mt-3 px-3 py-2 rounded-lg bg-zinc-900/80 border border-golden/15" data-testid="mood-verdict">
          <div className="flex items-start gap-2">
            <Sparkles className="w-3.5 h-3.5 text-golden flex-shrink-0 mt-0.5" />
            <p className="text-[12px] md:text-[13px] text-gray-200 leading-snug">
              {verdict.verdict}
            </p>
          </div>
        </div>
      )}
      {sentiment?.pcr !== undefined && (
        <div className="mt-3 pt-3 border-t border-zinc-800 grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-[10px] text-gray-500 uppercase">PCR</div>
            <div className="text-white font-semibold text-sm">{sentiment.pcr}</div>
          </div>
          <div>
            <div className="text-[10px] text-gray-500 uppercase">VIX</div>
            <div className="text-white font-semibold text-sm">{vix?.ltp != null ? Number(vix.ltp).toFixed(2) : '—'}</div>
          </div>
          <div>
            <div className="text-[10px] text-gray-500 uppercase">A/D</div>
            <div className="text-white font-semibold text-sm">{breadth?.advance_decline_ratio != null ? Number(breadth.advance_decline_ratio).toFixed(2) : '—'}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const SectionTitle = ({ icon: Icon, title, hint, children }) => (
  <div className="flex items-center justify-between mb-4">
    <div className="flex items-center gap-2">
      <Icon className="w-5 h-5 text-golden" />
      <h2 className="text-lg md:text-xl font-bold text-white">{title}</h2>
      {hint && <span className="text-xs text-gray-400 font-normal hidden md:inline">{hint}</span>}
    </div>
    {children}
  </div>
);

// Horizontal bar chart ranking sectors by today's % change. Red for losers, green for gainers.
const SectorStrengthChart = ({ indices, sectors }) => {
  const data = useMemo(() => {
    return sectors
      .map(name => {
        const d = indices[name];
        return d ? { name: name.replace('NIFTY ', ''), change: Number(d.change_percent) || 0 } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.change - a.change);
  }, [indices, sectors]);

  if (data.length === 0) return null;

  return (
    <div className="rounded-xl border border-golden/20 bg-zinc-900/60 p-4" data-testid="sector-strength-chart">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-white font-semibold text-sm">Sector Strength · Today</h3>
        <span className="text-[10px] text-gray-500 uppercase tracking-wider">% Change</span>
      </div>
      <div style={{ width: '100%', height: data.length * 28 + 30 }}>
        <ResponsiveContainer>
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 40, left: 20, bottom: 5 }}>
            <CartesianGrid stroke="#27272a" strokeDasharray="2 4" horizontal={false} />
            <XAxis type="number" stroke="#71717a" fontSize={10} tickFormatter={(v) => `${v}%`} />
            <YAxis type="category" dataKey="name" stroke="#a1a1aa" fontSize={11} width={90} />
            <Tooltip
              cursor={{ fill: 'rgba(212,168,67,0.05)' }}
              contentStyle={{ background: '#0c0c0c', border: '1px solid #d4a843', borderRadius: 8, fontSize: 12 }}
              formatter={(v) => [`${Number(v).toFixed(2)}%`, 'Change']}
            />
            <Bar dataKey="change" radius={[0, 4, 4, 0]} barSize={16}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.change >= 0 ? '#10b981' : '#ef4444'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Donut chart split of gainers/losers/unchanged in the broader market
const BreadthDonut = ({ data }) => {
  const points = useMemo(() => {
    if (!data) return [];
    return [
      { name: 'Advances', value: data.advances || 0, color: '#10b981' },
      { name: 'Declines', value: data.declines || 0, color: '#ef4444' },
      { name: 'Unchanged', value: data.unchanged || 0, color: '#71717a' },
    ].filter(p => p.value > 0);
  }, [data]);

  if (points.length === 0) return null;
  const total = points.reduce((s, p) => s + p.value, 0);

  return (
    <div className="rounded-xl border border-golden/20 bg-zinc-900/60 p-4" data-testid="breadth-donut">
      <h3 className="text-white font-semibold text-sm mb-2">Market Breadth · Donut</h3>
      <div style={{ width: '100%', height: 200 }}>
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={points}
              dataKey="value"
              nameKey="name"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={3}
              stroke="none"
            >
              {points.map((p, i) => <Cell key={i} fill={p.color} />)}
            </Pie>
            <Tooltip
              contentStyle={{ background: '#0c0c0c', border: '1px solid #d4a843', borderRadius: 8, fontSize: 12 }}
              formatter={(v, n) => [`${v} (${((v / total) * 100).toFixed(1)}%)`, n]}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Horizontal bar list of top gainers/losers — visualises magnitude vs the row table
const MoversBarList = ({ rows, positive }) => {
  const data = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return rows.slice(0, 5).map(r => ({
      name: (r.symbol || r.name || '—').slice(0, 14),
      change: Number(r.change_percent ?? r.changePercent ?? r.pct ?? 0),
    }));
  }, [rows]);
  if (data.length === 0) return null;
  return (
    <div style={{ width: '100%', height: 160 }} className="mt-2">
      <ResponsiveContainer>
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" stroke="#a1a1aa" fontSize={10} width={70} />
          <Tooltip
            cursor={{ fill: 'rgba(212,168,67,0.04)' }}
            contentStyle={{ background: '#0c0c0c', border: '1px solid #d4a843', borderRadius: 8, fontSize: 11 }}
            formatter={(v) => [`${Number(v).toFixed(2)}%`, 'Change']}
          />
          <Bar dataKey="change" radius={[0, 4, 4, 0]} fill={positive ? '#10b981' : '#ef4444'} barSize={14} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// --- Tabs ---
const TABS = [
  { id: 'ai', label: 'AI Outlook', icon: Bot },
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'global', label: 'Global Cues', icon: Globe },
  { id: 'movers', label: 'Movers & Breadth', icon: Flame },
  { id: 'circuits', label: 'UC / LC Radar', icon: Zap },
  { id: 'options', label: 'Options & FII/DII', icon: Activity },
  { id: 'trade_idea', label: 'AI Trade Idea', icon: Crown, premium: true },
  { id: 'deals', label: 'Block & Bulk', icon: Briefcase, premium: true },
  { id: 'events', label: 'Event Calendar', icon: Calendar, premium: true },
  { id: 'alerts', label: 'Smart Alerts', icon: Bell, premium: true },
];

const MarketOutlookDashboard = () => {
  const [tab, setTab] = useState('ai');
  const [data, setData] = useState(null);
  const [globalCues, setGlobalCues] = useState(null);
  const [movers, setMovers] = useState(null);
  const [breadth, setBreadth] = useState(null);
  const [ucLc, setUcLc] = useState(null);
  const [aiOutlook, setAiOutlook] = useState(null);
  const [marketStatus, setMarketStatus] = useState(null);
  const [access, setAccess] = useState({ logged_in: false, has_active_subscription: false });
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [md, gc, mv, br, ul, ms] = await Promise.allSettled([
        fetch(`${API_URL}/api/market-data`).then(r => r.json()),
        fetch(`${API_URL}/api/market-data/global-cues`).then(r => r.json()),
        fetch(`${API_URL}/api/market-data/movers`).then(r => r.json()),
        fetch(`${API_URL}/api/market-data/breadth`).then(r => r.json()),
        fetch(`${API_URL}/api/market-data/uc-lc-radar`).then(r => r.json()),
        fetch(`${API_URL}/api/market-data/status`).then(r => r.json()),
      ]);
      if (md.status === 'fulfilled') setData(md.value);
      if (gc.status === 'fulfilled') setGlobalCues(gc.value);
      if (mv.status === 'fulfilled') setMovers(mv.value);
      if (br.status === 'fulfilled') setBreadth(br.value);
      if (ul.status === 'fulfilled') setUcLc(ul.value);
      if (ms.status === 'fulfilled') setMarketStatus(ms.value);
      setLastFetch(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAi = useCallback(async (force = false) => {
    setAiLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/market-data/ai-outlook${force ? '?force=true' : ''}`);
      const j = await r.json();
      setAiOutlook(j);
    } finally {
      setAiLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    fetchAi(false);
    // Fetch access status (premium gating)
    const subToken = localStorage.getItem('subscriber_token');
    fetch(`${API_URL}/api/market-intelligence/access-status`, {
      headers: subToken ? { Authorization: `Bearer ${subToken}` } : {},
    }).then(r => r.json()).then(setAccess).catch(() => {});

    const t = setInterval(fetchAll, 15000); // 15s for live feel
    return () => clearInterval(t);
  }, [fetchAll, fetchAi]);

  const indices = data?.indices || {};
  const commodities = data?.commodities || {};
  const currency = data?.currency || {};
  const fiiDii = data?.fii_dii;
  const optionChain = data?.option_chain || {};
  const sentiment = data?.sentiment || { label: 'Neutral', score: 50 };

  const status = statusBadge(marketStatus?.status);
  const todayStr = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  }, []);

  // Sentiment color
  const sentColor = (() => {
    const s = sentiment.score || 50;
    if (s >= 70) return 'from-emerald-500 via-green-500 to-emerald-600';
    if (s >= 55) return 'from-green-500 via-emerald-500 to-green-600';
    if (s >= 45) return 'from-amber-500 via-yellow-500 to-amber-600';
    if (s >= 30) return 'from-orange-500 via-red-500 to-orange-600';
    return 'from-red-600 via-rose-600 to-red-700';
  })();

  // Ticker order
  const tickerOrder = ['NIFTY 50', 'SENSEX', 'BANK NIFTY', 'FIN NIFTY', 'INDIA VIX', 'GIFT NIFTY'];
  const tickerExtra = ['USD/INR'];

  return (
    <div className="min-h-screen bg-black text-white selection:bg-golden/40">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/90 backdrop-blur-md z-50 border-b border-golden/40">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="https://customer-assets.emergentagent.com/job_invest-links/artifacts/kydgz8tm_WhatsApp%20Image%202025-08-23%20at%203.47.06%20PM.jpeg"
              alt="Upper Circuit Stocks"
              className="h-10 md:h-12 w-auto"
            />
          </Link>
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/welcome" className="text-golden hover:text-golden-light transition-colors">About</Link>
            <Link to="/courses" className="text-golden hover:text-golden-light transition-colors">Courses</Link>
            <Link to="/free-learning" className="text-golden hover:text-golden-light transition-colors">Free Learning</Link>
            <Link to="/market-snapshot" className="text-golden hover:text-golden-light transition-colors">Daily Snapshot</Link>
            <Link to="/" className="text-golden-light font-semibold">Market Intelligence</Link>
            <Link to="/masterclass" className="text-golden hover:text-golden-light transition-colors" data-testid="dash-nav-masterclass">Masterclass</Link>
            <Link to="/workshop" className="text-golden hover:text-golden-light transition-colors" data-testid="dash-nav-workshop">Workshop</Link>
            <Link to="/login" className="bg-golden hover:bg-golden/90 text-black font-semibold px-4 py-2 rounded-lg transition-colors">Login</Link>
          </nav>
          <button className="md:hidden text-golden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-black border-t border-golden/30 py-2 space-y-1 px-4">
            <Link to="/welcome" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm">About</Link>
            <Link to="/courses" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm">Courses</Link>
            <Link to="/free-learning" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm">Free Learning</Link>
            <Link to="/market-snapshot" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm">Daily Snapshot</Link>
            <Link to="/masterclass" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm" data-testid="dash-mobile-nav-masterclass">Masterclass</Link>
            <Link to="/workshop" className="block text-golden hover:bg-golden/10 px-3 py-2.5 rounded-lg text-sm" data-testid="dash-mobile-nav-workshop">Workshop</Link>
            <Link to="/login" className="block bg-golden text-black font-semibold px-3 py-2.5 rounded-lg text-sm">Login</Link>
          </div>
        )}
      </header>

      {/* Title + Status Bar */}
      <div className="pt-20 px-4">
        <div className="container mx-auto">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="inline-flex items-center gap-2 bg-golden/10 border border-golden/30 rounded-full px-3 py-1 mb-2" data-testid="ai-badge">
                <Sparkles className="w-3.5 h-3.5 text-golden" />
                <span className="text-golden text-[11px] font-bold tracking-[0.2em] uppercase">AI Market Intelligence</span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white tracking-tight">
                Trading <span className="text-golden">Terminal</span>
              </h1>
              <div className="flex items-center gap-2 mt-2 text-xs md:text-sm text-gray-400 flex-wrap">
                <Clock className="w-3.5 h-3.5" />
                <span data-testid="terminal-date">{todayStr}</span>
                {marketStatus?.ist_time && <span className="text-gray-500 hidden sm:inline"> · {marketStatus.ist_time}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1.5 rounded-full text-xs font-bold border ${status.cls}`} data-testid="market-status-badge">
                {status.label}
              </span>
              <button
                onClick={fetchAll}
                className="bg-golden hover:bg-golden/90 text-black font-semibold px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>

          {/* Disclaimer banner — moved to bottom for less intrusion */}
        </div>
      </div>

      {/* Live Ticker */}
      <div className="mt-6 px-4">
        <div className="container mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
            </span>
            <span className="text-[11px] uppercase tracking-widest text-red-400 font-bold">Live Ticker</span>
            <span className="text-[10px] text-gray-500">· auto-refresh every 15s</span>
          </div>
          <div className="rounded-2xl border border-golden/20 bg-gradient-to-r from-zinc-950 via-zinc-900 to-zinc-950 p-3 overflow-x-auto">
            <div className="flex gap-3 min-w-max" data-testid="live-ticker">
              {tickerOrder.map(k => indices[k] && <StatChip key={k} label={k} data={indices[k]} />)}
              {tickerExtra.map(k => currency[k] && <StatChip key={k} label={k} data={currency[k]} />)}
              {(commodities['GOLD'] || commodities['CRUDE OIL']) && (
                <>
                  {commodities['GOLD'] && <StatChip key="GOLD" label="GOLD" data={commodities['GOLD']} />}
                  {commodities['CRUDE OIL'] && <StatChip key="CRUDE" label="CRUDE" data={commodities['CRUDE OIL']} />}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs — Desktop only */}
      <div className="mt-6 px-4 hidden md:block">
        <div className="container mx-auto">
          <div className="flex flex-wrap gap-2 border-b border-golden/15 pb-1" data-testid="tabs-nav">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              const locked = t.premium && !access.has_active_subscription;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg text-sm font-semibold transition-all ${
                    active
                      ? 'bg-golden/15 text-golden border-x border-t border-golden/30 -mb-px'
                      : 'text-gray-400 hover:text-golden/80 hover:bg-golden/5 border border-transparent'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{t.label}</span>
                  {t.premium && (
                    <span className={`ml-1 inline-flex items-center justify-center w-4 h-4 rounded text-[9px] font-bold ${locked ? 'bg-golden/20 text-golden border border-golden/40' : 'bg-emerald-500/20 text-emerald-300'}`}>
                      {locked ? '🔒' : '★'}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Mobile — Current Tab Sticky Header */}
      <div className="md:hidden mt-4 px-4">
        <div className="container mx-auto">
          <MobileTabSelector tab={tab} setTab={setTab} access={access} />
        </div>
      </div>

      {/* Tab Content */}
      <main className="px-4 py-6 md:py-8 pb-24 md:pb-8">
        <div className="container mx-auto space-y-8">

          {/* AI OUTLOOK */}
          {tab === 'ai' && (
            <div className="space-y-6">
              <div className="relative overflow-hidden rounded-2xl border border-golden/30 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-6 md:p-8" data-testid="ai-outlook-card">
                <div className="absolute top-0 right-0 w-72 h-72 bg-golden/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-72 h-72 bg-amber-500/5 rounded-full blur-3xl"></div>
                <div className="relative">
                  <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-golden to-amber-600 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-6 h-6 text-black" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white">UC Trader AI</h2>
                        <p className="text-sm text-gray-400">Claude AI · Auto-refreshes every 30 min</p>
                      </div>
                    </div>
                    <button
                      onClick={() => fetchAi(true)}
                      disabled={aiLoading}
                      className="bg-golden/20 hover:bg-golden/30 border border-golden/40 text-golden font-semibold px-4 py-2 rounded-lg flex items-center gap-2 text-sm disabled:opacity-50"
                      data-testid="ai-outlook-refresh-btn"
                    >
                      <RefreshCw className={`w-4 h-4 ${aiLoading ? 'animate-spin' : ''}`} />
                      {aiLoading ? 'Generating...' : 'Re-generate'}
                    </button>
                  </div>
                  {aiLoading && !aiOutlook && (
                    <div className="space-y-2 animate-pulse">
                      <div className="h-3 bg-golden/10 rounded w-3/4"></div>
                      <div className="h-3 bg-golden/10 rounded w-5/6"></div>
                      <div className="h-3 bg-golden/10 rounded w-2/3"></div>
                      <div className="h-3 bg-golden/10 rounded w-4/5"></div>
                    </div>
                  )}
                  {aiOutlook?.error && (
                    <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                      AI service: {aiOutlook.error}
                    </div>
                  )}
                  {aiOutlook?.outlook && (
                    <div className="prose prose-invert max-w-none text-gray-200 leading-relaxed">
                      <FormattedOutlook text={aiOutlook.outlook} />
                    </div>
                  )}
                  {aiOutlook?.generated_at && (
                    <div className="text-xs text-gray-500 mt-4">
                      Generated at {new Date(aiOutlook.generated_at).toLocaleString('en-IN')}
                    </div>
                  )}
                </div>
              </div>

              {/* Sentiment + Primary Indices side by side */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className={`md:col-span-1 rounded-2xl bg-gradient-to-br ${sentColor} p-5 shadow-2xl`} data-testid="market-sentiment-card">
                  <div className="text-white/80 text-xs font-bold uppercase tracking-widest">Market Sentiment</div>
                  <div className="text-white text-3xl font-bold mt-1">{sentiment.label}</div>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-white/80 text-sm">Score</div>
                    <div className="text-white text-4xl font-bold">{sentiment.score}<span className="text-xl opacity-70">/100</span></div>
                  </div>
                  {sentiment.pcr !== undefined && (
                    <div className="mt-3 pt-3 border-t border-white/20 text-white/90 text-sm">
                      Nifty PCR: <span className="font-bold">{sentiment.pcr}</span>
                    </div>
                  )}
                </div>
                <div className="md:col-span-2 grid grid-cols-2 gap-3">
                  {['NIFTY 50', 'BANK NIFTY', 'SENSEX', 'INDIA VIX'].map(k => (
                    indices[k] && <IndexCard key={k} label={k} data={indices[k]} accent />
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* OVERVIEW */}
          {tab === 'overview' && (
            <div className="space-y-8">
              {/* Daily Trader Quote — also surfaced here for quick share */}
              <DailyTraderQuoteSlim />

              {/* Mood Index + Primary Indices */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1">
                  <MarketMoodIndex
                    sentiment={sentiment}
                    breadth={breadth?.breadth}
                    vix={indices['INDIA VIX']}
                  />
                </div>
                <div className="lg:col-span-2">
                  <SectionTitle icon={BarChart3} title="Primary Indices" />
                  <div className="grid grid-cols-2 gap-3">
                    {['NIFTY 50', 'BANK NIFTY', 'SENSEX', 'FIN NIFTY'].map(k =>
                      indices[k] && <IndexCard key={k} label={k} data={indices[k]} accent />
                    )}
                  </div>
                </div>
              </div>
              <div>
                <SectionTitle icon={Target} title="Sectoral Indices" hint="11 NSE sectors" />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {['NIFTY IT', 'NIFTY AUTO', 'NIFTY PHARMA', 'NIFTY FMCG',
                    'NIFTY METAL', 'NIFTY REALTY', 'NIFTY ENERGY', 'NIFTY PSU BANK',
                    'NIFTY MIDCAP 100', 'NIFTY SMALLCAP 100'
                  ].map(k => indices[k] && <IndexCard key={k} label={k} data={indices[k]} />)}
                </div>
                <div className="mt-4">
                  <SectorStrengthChart
                    indices={indices}
                    sectors={[
                      'NIFTY IT', 'NIFTY AUTO', 'NIFTY PHARMA', 'NIFTY FMCG',
                      'NIFTY METAL', 'NIFTY REALTY', 'NIFTY ENERGY', 'NIFTY PSU BANK',
                      'NIFTY MIDCAP 100', 'NIFTY SMALLCAP 100'
                    ]}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <SectionTitle icon={Sparkles} title="Commodities" />
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.keys(commodities).map(k => <IndexCard key={k} label={k} data={commodities[k]} />)}
                  </div>
                </div>
                <div>
                  <SectionTitle icon={IndianRupee} title="Currency" />
                  <div className="grid gap-3">
                    {Object.keys(currency).map(k => <IndexCard key={k} label={k} data={currency[k]} />)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* GLOBAL CUES */}
          {tab === 'global' && (
            <div className="space-y-6" data-testid="global-cues-section">
              <SectionTitle icon={Globe} title="Global Market Cues" hint="Pre-market direction indicators" />
              {globalCues?.items && (
                <>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">🇮🇳 Indian Pre-Market</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {globalCues.items['GIFT NIFTY'] && globalCues.items['GIFT NIFTY'].ltp
                        ? (
                          <div className="relative">
                            <IndexCard label="GIFT NIFTY" data={globalCues.items['GIFT NIFTY']} accent />
                            {globalCues.items['GIFT NIFTY'].stale && (
                              <div className="absolute top-2 right-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/40">
                                Last synced · {globalCues.items['GIFT NIFTY'].as_of
                                  ? new Date(globalCues.items['GIFT NIFTY'].as_of).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                                  : '—'}
                              </div>
                            )}
                          </div>
                        )
                        : (
                          <div className="rounded-xl border border-dashed border-golden/30 bg-zinc-900/40 p-4" data-testid="gift-nifty-pending">
                            <div className="text-golden/80 text-[11px] font-semibold tracking-wider uppercase">GIFT NIFTY</div>
                            <div className="text-gray-400 text-sm mt-2">Live feed unavailable — Yahoo Finance does not publish GIFT NIFTY. Auto-syncs from Fyers daily at 8:45 AM IST.</div>
                            <div className="text-[10px] text-gray-500 mt-1">Tip: Trigger manual sync from Admin → Settings → Fyers.</div>
                          </div>
                        )}
                      {indices['INDIA VIX'] && <IndexCard label="INDIA VIX" data={indices['INDIA VIX']} />}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">🇺🇸 US Markets</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['DOW JONES', 'S&P 500', 'NASDAQ'].map(k =>
                        globalCues.items[k] && <IndexCard key={k} label={k} data={globalCues.items[k]} />)}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">🌏 Asian Markets</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['NIKKEI 225', 'HANG SENG', 'SHANGHAI'].map(k =>
                        globalCues.items[k] && <IndexCard key={k} label={k} data={globalCues.items[k]} />)}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">🇪🇺 European Markets</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {['FTSE 100', 'DAX'].map(k =>
                        globalCues.items[k] && <IndexCard key={k} label={k} data={globalCues.items[k]} />)}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">₿ Crypto & Bonds</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {['BITCOIN', 'ETHEREUM', 'US 10Y YIELD', 'DOLLAR INDEX'].map(k =>
                        globalCues.items[k] && <IndexCard key={k} label={k} data={globalCues.items[k]} />)}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* MOVERS */}
          {tab === 'movers' && (
            <div className="space-y-6">
              <SectionTitle icon={Flame} title="Top Movers" hint="Nifty 50 — last completed session" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="movers-section">
                <MoversTable title="Top Gainers" rows={movers?.gainers || []} positive tone="emerald" testId="top-gainers" />
                <MoversTable title="Top Losers" rows={movers?.losers || []} positive={false} tone="red" testId="top-losers" />
              </div>
              <SectionTitle icon={Activity} title="Most Active by Turnover" hint="Heavy institutional interest" />
              <MoversTable title="Most Active" rows={movers?.most_active || []} positive showTurnover tone="amber" testId="most-active" />

              <SectionTitle icon={BarChart3} title="Market Breadth (Nifty 50)" />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <BreadthCard data={breadth?.breadth} />
                <BreadthDonut data={breadth?.breadth} />
              </div>
            </div>
          )}

          {/* UC/LC RADAR */}
          {tab === 'circuits' && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-golden/10 via-amber-500/5 to-transparent border border-golden/30 rounded-2xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <Zap className="w-6 h-6 text-golden" />
                  <h2 className="text-2xl font-bold text-white">Upper Circuit / Lower Circuit Radar</h2>
                </div>
                <p className="text-sm text-gray-400">
                  Stocks that hit their daily price band — strong sentiment signals & potential momentum candidates.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <CircuitList
                  title="Upper Circuit (UC)"
                  subtitle="Stocks hitting upper band"
                  icon={ArrowUpRight}
                  tone="emerald"
                  rows={ucLc?.data?.upper_circuit || []}
                  testId="uc-list"
                />
                <CircuitList
                  title="Lower Circuit (LC)"
                  subtitle="Stocks hitting lower band"
                  icon={ArrowDownRight}
                  tone="red"
                  rows={ucLc?.data?.lower_circuit || []}
                  testId="lc-list"
                />
              </div>
            </div>
          )}

          {/* OPTIONS & FII/DII */}
          {tab === 'options' && (
            <div className="space-y-8">
              <SectionTitle icon={Activity} title="Option Chain Insights" hint="Powered by Fyers (when active)" />
              {Object.keys(optionChain).length === 0 ? (
                <div className="rounded-xl border border-dashed border-golden/20 bg-zinc-900/40 p-6 text-center text-gray-400 text-sm">
                  Option-chain data is available when admin sets the daily Fyers token.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(optionChain).map(([label, oc]) => (
                    <div key={label} className="rounded-xl border border-golden/20 bg-gradient-to-br from-zinc-900 to-black p-5" data-testid={`option-chain-${label.toLowerCase()}`}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="text-white font-bold text-lg">{label}</div>
                        {oc.expiry && <span className="text-xs text-golden bg-golden/10 border border-golden/30 px-2 py-0.5 rounded">Exp: {oc.expiry}</span>}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <Metric label="PCR (OI)" value={oc.pcr_oi} />
                        <Metric label="PCR (Vol)" value={oc.pcr_volume} />
                        <Metric label="Strong Support" value={oc.max_put_oi_strike} tone="emerald" hint="Max PE OI" />
                        <Metric label="Strong Resistance" value={oc.max_call_oi_strike} tone="red" hint="Max CE OI" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <SectionTitle icon={Globe} title="FII / DII Activity" hint="Institutional flow" />
              {!fiiDii ? (
                <div className="text-gray-400 text-sm">FII/DII data not available right now.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {['fii', 'dii'].map(key => {
                    const x = fiiDii[key];
                    if (!x) return null;
                    const net = Number(x.net_value || 0);
                    const positive = net >= 0;
                    return (
                      <div key={key} className="rounded-xl border border-golden/20 bg-gradient-to-br from-zinc-900 to-black p-5" data-testid={`fii-dii-${key}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-white font-bold text-lg uppercase">{key === 'fii' ? 'FII / FPI' : 'DII'}</div>
                          <span className="text-xs text-gray-400">{x.date}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <Metric label="Buy" value={fmtCr(x.buy_value)} raw />
                          <Metric label="Sell" value={fmtCr(x.sell_value)} raw />
                          <div>
                            <div className="text-xs text-gray-400 uppercase tracking-wider">Net</div>
                            <div className={`text-base font-bold mt-1 ${positive ? 'text-emerald-400' : 'text-red-400'} flex items-center gap-1`}>
                              {positive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                              {fmtCr(x.net_value)}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PREMIUM: AI TRADE IDEA */}
          {tab === 'trade_idea' && <TradeIdeaTab access={access} />}

          {/* PREMIUM: BLOCK / BULK DEALS */}
          {tab === 'deals' && <BlockDealsTab access={access} />}

          {/* PREMIUM: EVENT CALENDAR */}
          {tab === 'events' && <EventCalendarTab access={access} />}

          {/* PREMIUM: SMART ALERTS */}
          {tab === 'alerts' && <SmartAlertsTab access={access} />}

        </div>
      </main>

      {/* Subscription CTA */}
      <section className="px-4 pb-12">
        <div className="container mx-auto">
          <div className="rounded-2xl bg-gradient-to-r from-golden via-yellow-500 to-amber-500 p-[1px]">
            <div className="bg-black rounded-2xl p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-full bg-golden/20 border border-golden/40 flex items-center justify-center flex-shrink-0">
                  <Crown className="w-7 h-7 text-golden" />
                </div>
                <div>
                  <div className="text-golden text-xs uppercase tracking-widest font-semibold mb-1">Unlock Premium</div>
                  <h3 className="text-white text-2xl md:text-3xl font-bold">Learn the method behind the numbers.</h3>
                  <p className="text-gray-300 mt-2 text-sm md:text-base max-w-xl">
                    Monthly educational modules on options, futures and equity — built from historical market data,
                    with a private Telegram study group and monthly Q&amp;A.
                  </p>
                </div>
              </div>
              <Link to="/welcome#services" className="bg-golden hover:bg-golden/90 text-black font-bold px-6 py-3 rounded-lg flex items-center gap-2 whitespace-nowrap" data-testid="cta-explore-plans-btn">
                Explore Plans <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>

          {/* Workshop promo */}
          <div className="mt-5 rounded-2xl border border-golden/30 bg-gradient-to-br from-[#151009] to-black p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-5" data-testid="workshop-promo-card">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-golden/15 border border-golden/40 flex items-center justify-center flex-shrink-0">
                <Zap className="w-7 h-7 text-golden" />
              </div>
              <div>
                <div className="text-golden text-xs uppercase tracking-widest font-semibold mb-1">2-Day Live Workshop · 24–25 Sept</div>
                <h3 className="text-white text-xl md:text-2xl font-bold">Stock Options Strategy Workshop</h3>
                <p className="text-gray-300 mt-2 text-sm md:text-base max-w-xl">
                  Live intraday options setups, 2+ ready-to-use scanners and a premium Telegram community.
                  <span className="text-golden-light font-semibold"> ₹15,000</span>
                  <span className="text-gray-500 line-through ml-2">₹20,000</span>
                </p>
              </div>
            </div>
            <Link to="/workshop" className="bg-golden hover:bg-golden/90 text-black font-bold px-6 py-3 rounded-lg flex items-center gap-2 whitespace-nowrap" data-testid="cta-workshop-btn">
              Join the Workshop <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
      <div className="px-4 pb-8 text-center text-xs text-gray-500">
        <div className="container mx-auto flex items-center justify-center gap-2 flex-wrap">
          <Wifi className="w-3.5 h-3.5" />
          <span>Sources: {data?.fyers_active ? 'Fyers (live) + ' : ''}Yahoo Finance + NSE</span>
          {lastFetch && <span>· Last updated: {lastFetch.toLocaleTimeString()}</span>}
        </div>
        <div className="container mx-auto mt-3 flex items-start justify-center gap-1.5 text-[10px] md:text-[11px] text-gray-600 leading-snug max-w-3xl" data-testid="market-disclaimer-banner">
          <AlertTriangle className="w-3 h-3 text-gray-500 mt-0.5 flex-shrink-0" />
          <p>
            <span className="font-semibold text-gray-500">Disclaimer:</span> Data is indicative, fetched from public sources & broker APIs with a slight delay. For educational purposes only — not investment advice. Trading involves substantial risk.
          </p>
        </div>
      </div>

      {/* Mobile Bottom Tab Bar */}
      <MobileBottomTabBar tab={tab} setTab={setTab} access={access} />
    </div>
  );
};

// --- Sub-components ---

const Metric = ({ label, value, tone, hint, raw }) => {
  const toneCls = tone === 'emerald' ? 'text-emerald-300' : tone === 'red' ? 'text-red-300' : 'text-white';
  return (
    <div>
      <div className={`text-xs ${tone === 'emerald' ? 'text-emerald-400' : tone === 'red' ? 'text-red-400' : 'text-gray-400'} uppercase tracking-wider`}>
        {label}
      </div>
      <div className={`text-xl font-bold ${toneCls} mt-1`}>
        {raw ? value : (value === null || value === undefined ? '—' : value)}
      </div>
      {hint && <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>}
    </div>
  );
};

const MoversTable = ({ title, rows, tone, showTurnover, testId }) => {
  const toneAccent = {
    emerald: 'border-emerald-500/30 from-emerald-900/20',
    red: 'border-red-500/30 from-red-900/20',
    amber: 'border-amber-500/30 from-amber-900/20',
  }[tone] || 'border-golden/20';
  return (
    <div className={`rounded-xl border ${toneAccent} bg-gradient-to-br to-black p-4`} data-testid={testId}>
      <h3 className="text-white font-bold mb-3">{title}</h3>
      {rows.length === 0 ? (
        <div className="text-gray-500 text-sm">No data yet</div>
      ) : (
        <>
          <div className="space-y-2">
            {rows.map((r) => {
              const positive = (r.change_percent ?? 0) >= 0;
              return (
                <div key={r.symbol} className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0">
                  <div className="font-semibold text-white text-sm truncate">{r.symbol}</div>
                  <div className="flex items-center gap-3 text-sm flex-shrink-0">
                    <span className="text-gray-300">₹{fmt(r.ltp)}</span>
                    <span className={`${positive ? 'text-emerald-400' : 'text-red-400'} font-bold w-16 text-right`}>
                      {positive ? '+' : ''}{Number(r.change_percent || 0).toFixed(2)}%
                    </span>
                    {showTurnover && (
                      <span className="text-gray-400 text-xs hidden md:inline">₹{fmtInt(Math.round((r.turnover || 0) / 10000000))}Cr</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {!showTurnover && <MoversBarList rows={rows} positive={tone === 'emerald'} />}
        </>
      )}
    </div>
  );
};

const BreadthCard = ({ data }) => {
  if (!data) return <div className="text-gray-400 text-sm">Breadth data unavailable</div>;
  const total = data.total || 1;
  const advPct = ((data.advances / total) * 100).toFixed(0);
  const decPct = ((data.declines / total) * 100).toFixed(0);
  const ratio = data.advance_decline_ratio;
  const verdict = ratio === null ? 'Neutral'
    : ratio >= 2 ? 'Strongly Bullish breadth'
    : ratio >= 1.2 ? 'Bullish breadth'
    : ratio >= 0.8 ? 'Neutral breadth'
    : ratio >= 0.5 ? 'Bearish breadth'
    : 'Strongly Bearish breadth';
  return (
    <div className="rounded-xl border border-golden/20 bg-zinc-900/60 p-5" data-testid="breadth-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-white font-bold">{data.scope || 'Market'} Advance/Decline</div>
        <div className="text-golden text-sm font-semibold">A/D Ratio: {ratio ?? '—'}</div>
      </div>
      <div className="h-3 rounded-full overflow-hidden bg-zinc-800 flex" data-testid="breadth-bar">
        <div className="bg-emerald-500" style={{ width: `${advPct}%` }} title={`Advances ${advPct}%`}></div>
        <div className="bg-red-500" style={{ width: `${decPct}%` }} title={`Declines ${decPct}%`}></div>
      </div>
      <div className="flex justify-between mt-2 text-sm">
        <span className="text-emerald-400 font-semibold">▲ {data.advances} Adv</span>
        <span className="text-gray-400">— {data.unchanged} Unch</span>
        <span className="text-red-400 font-semibold">▼ {data.declines} Dec</span>
      </div>
      <div className="mt-3 text-xs text-gray-400">{verdict}</div>
    </div>
  );
};

const CircuitList = ({ title, subtitle, icon: Icon, tone, rows, testId }) => {
  const toneCls = tone === 'emerald'
    ? 'border-emerald-500/30 from-emerald-900/20 text-emerald-300'
    : 'border-red-500/30 from-red-900/20 text-red-300';
  return (
    <div className={`rounded-xl border ${toneCls.split(' ').slice(0, 2).join(' ')} bg-gradient-to-br ${toneCls.split(' ')[1]} to-black p-4`} data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${toneCls.split(' ')[2]}`} />
            <h3 className="text-white font-bold">{title}</h3>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
        </div>
        <span className="text-2xl font-bold text-white">{rows.length}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-gray-500 text-sm py-4 text-center">No stocks hit circuit</div>
      ) : (
        <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
          {rows.map((r) => (
            <div key={r.symbol} className="flex items-center justify-between bg-black/40 rounded-md px-3 py-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-white text-sm font-semibold truncate">{r.symbol}</span>
                {r.band_pct && <span className="text-[10px] text-golden bg-golden/10 border border-golden/30 px-1.5 py-0.5 rounded">{r.band_pct}%</span>}
              </div>
              <div className="flex items-center gap-2 text-sm flex-shrink-0">
                <span className="text-gray-300">₹{fmt(r.ltp)}</span>
                <span className={`${toneCls.split(' ')[2]} font-bold w-14 text-right`}>
                  {(r.change_percent ?? 0) >= 0 ? '+' : ''}{Number(r.change_percent || 0).toFixed(2)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Lightweight markdown-ish formatter for the AI outlook (handles ** and bullets)
const FormattedOutlook = ({ text }) => {
  const lines = (text || '').split('\n').filter(l => l.trim() !== '');
  return (
    <div className="space-y-2" data-testid="ai-outlook-content">
      {lines.map((line, i) => {
        const isBullet = /^\s*[-*]\s+/.test(line);
        const cleaned = line.replace(/^\s*[-*]\s+/, '');
        // Italic emphasis like *...*
        const html = cleaned
          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-golden">$1</strong>')
          .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em class="text-gray-400">$1</em>')
          .replace(/^# (.+)$/g, '<span class="text-xl font-bold text-white block mb-1">$1</span>')
          .replace(/^## (.+)$/g, '<span class="text-lg font-semibold text-white block mb-1">$1</span>');
        return isBullet ? (
          <div key={i} className="flex gap-2 text-sm md:text-base">
            <span className="text-golden flex-shrink-0">›</span>
            <span dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        ) : (
          <p key={i} className="text-sm md:text-base" dangerouslySetInnerHTML={{ __html: html }} />
        );
      })}
    </div>
  );
};

export default MarketOutlookDashboard;

// ============================================================
// Mobile-only components
// ============================================================
const MobileTabSelector = ({ tab, setTab, access }) => {
  const [open, setOpen] = React.useState(false);
  const current = TABS.find(t => t.id === tab) || TABS[0];
  const CurrentIcon = current.icon;
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between gap-2 bg-zinc-900 border border-golden/30 rounded-xl px-4 py-3 text-left"
        data-testid="mobile-tab-selector"
      >
        <span className="flex items-center gap-2">
          <CurrentIcon className="w-5 h-5 text-golden" />
          <span className="text-white font-semibold">{current.label}</span>
          {current.premium && !access?.has_active_subscription && <span className="text-[10px] bg-golden/20 text-golden border border-golden/40 px-1.5 py-0.5 rounded">🔒</span>}
        </span>
        <span className="text-gray-400 text-xs">Tap to switch ›</span>
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end" onClick={() => setOpen(false)} data-testid="mobile-tab-sheet">
          <div className="bg-zinc-950 border-t border-golden/30 rounded-t-3xl w-full max-h-[80vh] overflow-y-auto p-4 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-1 bg-golden/30 rounded-full mx-auto mb-4"></div>
            <h3 className="text-white font-bold mb-3">Choose a section</h3>
            <div className="grid grid-cols-2 gap-2">
              {TABS.map(t => {
                const Icon = t.icon;
                const active = tab === t.id;
                const locked = t.premium && !access?.has_active_subscription;
                return (
                  <button
                    key={t.id}
                    onClick={() => { setTab(t.id); setOpen(false); }}
                    data-testid={`mobile-tab-${t.id}`}
                    className={`flex flex-col items-start gap-1 px-3 py-3 rounded-xl border transition-all min-h-[68px] ${
                      active
                        ? 'bg-golden/15 border-golden/50 text-golden'
                        : 'bg-zinc-900 border-golden/15 text-gray-300 active:bg-golden/10'
                    }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <Icon className="w-4 h-4" />
                      {t.premium && <span className={`text-[9px] px-1 rounded ${locked ? 'bg-golden/20 text-golden' : 'bg-emerald-500/20 text-emerald-300'}`}>{locked ? '🔒' : '★'}</span>}
                    </span>
                    <span className="text-sm font-semibold">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MobileBottomTabBar = ({ tab, setTab, access }) => {
  // Show 4 quick tabs at the bottom + a "More" button for the rest
  const quickTabs = ['ai', 'overview', 'movers', 'circuits'];
  const [moreOpen, setMoreOpen] = React.useState(false);

  return (
    <>
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-t border-golden/30 grid grid-cols-5 gap-0"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0)' }}
        data-testid="mobile-bottom-tabbar"
      >
        {quickTabs.map(id => {
          const t = TABS.find(x => x.id === id);
          if (!t) return null;
          const Icon = t.icon;
          const active = tab === id;
          return (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`flex flex-col items-center justify-center py-2.5 gap-0.5 transition-colors ${active ? 'text-golden' : 'text-gray-400 active:text-golden/80'}`}
              data-testid={`bottom-tab-${id}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] font-semibold leading-none">{t.label.split(' ')[0]}</span>
              {active && <span className="w-1 h-1 bg-golden rounded-full mt-0.5"></span>}
            </button>
          );
        })}
        <button
          onClick={() => setMoreOpen(true)}
          className={`flex flex-col items-center justify-center py-2.5 gap-0.5 transition-colors ${['global', 'options', 'trade_idea', 'deals', 'events', 'alerts'].includes(tab) ? 'text-golden' : 'text-gray-400'}`}
          data-testid="bottom-tab-more"
        >
          <Menu className="w-5 h-5" />
          <span className="text-[10px] font-semibold leading-none">More</span>
          {!access?.has_active_subscription && (
            <span className="absolute top-1 right-3 w-2 h-2 bg-golden rounded-full animate-pulse"></span>
          )}
        </button>
      </nav>

      {moreOpen && (
        <div className="md:hidden fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-end" onClick={() => setMoreOpen(false)} data-testid="bottom-more-sheet">
          <div className="bg-zinc-950 border-t border-golden/30 rounded-t-3xl w-full max-h-[70vh] overflow-y-auto p-4 pb-8" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-1 bg-golden/30 rounded-full mx-auto mb-4"></div>
            <h3 className="text-white font-bold mb-1">More sections</h3>
            <p className="text-xs text-gray-400 mb-3">Premium features unlock with any active subscription</p>
            <div className="space-y-2">
              {['global', 'options', 'trade_idea', 'deals', 'events', 'alerts'].map(id => {
                const t = TABS.find(x => x.id === id);
                if (!t) return null;
                const Icon = t.icon;
                const locked = t.premium && !access?.has_active_subscription;
                return (
                  <button
                    key={id}
                    onClick={() => { setTab(id); setMoreOpen(false); }}
                    className="w-full flex items-center justify-between bg-zinc-900 hover:bg-zinc-800 active:bg-golden/10 border border-golden/15 rounded-xl px-4 py-3 text-left"
                    data-testid={`bottom-more-${id}`}
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-golden" />
                      <span className="text-white font-semibold">{t.label}</span>
                    </span>
                    {t.premium && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${locked ? 'bg-golden/20 text-golden border border-golden/40' : 'bg-emerald-500/20 text-emerald-300'}`}>
                        {locked ? '🔒 Locked' : '★ Premium'}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
